import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import './CoveredCalls.css';

function CoveredCalls({ username, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedBroker, setSelectedBroker] = useState('all');
  const [accounts, setAccounts] = useState([]);
  const [dateRange, setDateRange] = useState('last_1_year');
  const [groupBy, setGroupBy] = useState('week'); // week, month, year, underlying_symbol, none
  const [data, setData] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Track which groups are expanded
  const [expandedSymbols, setExpandedSymbols] = useState(new Set()); // Track which underlying_symbol groups are expanded (format: "group_key-symbol")

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    fetchCoveredCalls();
    // Reset expanded groups when groupBy changes
    setExpandedGroups(new Set());
    setExpandedSymbols(new Set());
  }, [selectedAccount, selectedBroker, dateRange, groupBy]);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio/accounts/?page_size=all`);
      const accountsData = response.data.results || response.data;
      setAccounts(Array.isArray(accountsData) ? accountsData : []);
    } catch (err) {
      setError('Error fetching accounts: ' + err.message);
      console.error(err);
    }
  };

  const fetchCoveredCalls = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `${API_URL}/portfolio/trades/covered_calls/`;
      const params = new URLSearchParams();
      
      if (selectedBroker && selectedBroker !== 'all') {
        params.append('broker', selectedBroker);
      }

      if (selectedAccount && selectedAccount !== 'all') {
        const account = accounts.find(acc =>
          acc.id === parseInt(selectedAccount) || acc.broker_account_id === selectedAccount
        );
        if (account) {
          params.append('account_id', account.broker_account_id);
        }
      }
      
      // Add date range filter
      const dateFilter = getDateRangeFilter();
      if (dateFilter) {
        const { from, to } = dateFilter;
        if (from) {
          params.append('date_from', from);
        }
        if (to) {
          params.append('date_to', to);
        }
      }
      
      // Add group_by parameter (always send it, even if 'none')
      params.append('group_by', groupBy);
      
      if (params.toString()) {
        url += '?' + params.toString();
      }
      
      const response = await axios.get(url);
      setData(response.data);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        setError('Session expired. Please log in again.');
      } else {
        setError('Error fetching covered calls: ' + err.message);
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getDateRangeFilter = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (dateRange) {
      case 'last_7_days':
        const last7Days = new Date(today);
        last7Days.setDate(last7Days.getDate() - 7);
        return {
          from: last7Days.toISOString().split('T')[0],
          to: today.toISOString().split('T')[0]
        };
      
      case 'last_30_days':
        const last30Days = new Date(today);
        last30Days.setDate(last30Days.getDate() - 30);
        return {
          from: last30Days.toISOString().split('T')[0],
          to: today.toISOString().split('T')[0]
        };
      
      case 'past_month':
        const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayOfPreviousMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        return {
          from: previousMonth.toISOString().split('T')[0],
          to: lastDayOfPreviousMonth.toISOString().split('T')[0]
        };
      
      case 'this_month':
        const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
          from: currentMonthStart.toISOString().split('T')[0],
          to: today.toISOString().split('T')[0]
        };
      
      case 'last_3_months':
        const last3Months = new Date(today);
        last3Months.setMonth(last3Months.getMonth() - 3);
        return {
          from: last3Months.toISOString().split('T')[0],
          to: today.toISOString().split('T')[0]
        };
      
      case 'last_1_year':
        const last1Year = new Date(today);
        last1Year.setFullYear(last1Year.getFullYear() - 1);
        return {
          from: last1Year.toISOString().split('T')[0],
          to: today.toISOString().split('T')[0]
        };
      
      case 'ytd':
        const yearStart = new Date(today.getFullYear(), 0, 1);
        return {
          from: yearStart.toISOString().split('T')[0],
          to: today.toISOString().split('T')[0]
        };
      
      case 'all':
      default:
        return null;
    }
  };

  const currencySymbol = selectedBroker === 'tastytrade' ? '$' : '€';

  const formatCurrency = (value) => {
    if (!value || value === '0.00') return `${currencySymbol} 0.00`;
    const num = parseFloat(value);
    const formattedNumber = num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${currencySymbol} ${formattedNumber}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Try to parse DD/MM/YY format
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      return dateStr; // Return as-is for DD/MM/YY
    }
    // Try ISO format
    try {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch (e) {
      // Ignore
    }
    return dateStr;
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '-';
    return `${parseFloat(value).toFixed(1)}%`;
  };

  const toggleGroup = (groupKey) => {
    const next = new Set(expandedGroups);
    if (next.has(groupKey)) {
      next.delete(groupKey);
      // Also collapse all underlying_symbol groups for this period
      const nextSymbols = new Set(expandedSymbols);
      const symbolKeys = Array.from(nextSymbols).filter(key => key.startsWith(`${groupKey}-`));
      symbolKeys.forEach(key => nextSymbols.delete(key));
      setExpandedSymbols(nextSymbols);
    } else {
      next.add(groupKey);
    }
    setExpandedGroups(next);
  };

  const toggleSymbolGroup = (groupKey, underlyingSymbol) => {
    const symbolKey = `${groupKey}-${underlyingSymbol}`;
    const next = new Set(expandedSymbols);
    if (next.has(symbolKey)) {
      next.delete(symbolKey);
    } else {
      next.add(symbolKey);
    }
    setExpandedSymbols(next);
  };

  if (loading) {
    return <div className="covered-calls-container"><div className="loading">Loading...</div></div>;
  }

  if (error) {
    return <div className="covered-calls-container"><div className="error">{error}</div></div>;
  }

  if (!data) {
    return <div className="covered-calls-container"><div className="empty-state">No data available</div></div>;
  }

  return (
    <div className="covered-calls-container">
      <div className="covered-calls-header">
        <h2>Covered Calls</h2>
        <p>Overview of profits and losses from closed covered calls</p>
      </div>

      <div className="covered-calls-content">
        <div className="covered-calls-controls">
          <div className="broker-selector">
            <label htmlFor="broker-select">Broker: </label>
            <select
              id="broker-select"
              value={selectedBroker}
              onChange={(e) => { setSelectedBroker(e.target.value); setSelectedAccount('all'); }}
            >
              <option value="all">All brokers</option>
              <option value="ibkr">IBKR</option>
              <option value="tastytrade">TastyTrade</option>
            </select>
          </div>

          {accounts.length > 0 && (
            <div className="account-selector">
              <label htmlFor="account-select">Account: </label>
              <select
                id="account-select"
                value={selectedAccount || 'all'}
                onChange={(e) => setSelectedAccount(e.target.value)}
              >
                <option value="all">All accounts</option>
                {accounts
                  .filter(acc => selectedBroker === 'all' || acc.broker === selectedBroker)
                  .map(account => (
                    <option key={account.id} value={account.id}>
                      {account.broker !== selectedBroker && selectedBroker === 'all'
                        ? `${account.broker.toUpperCase()} · ${account.display_name || account.broker_account_id}`
                        : account.display_name || account.broker_account_id}
                    </option>
                  ))}
              </select>
            </div>
          )}
          
          <div className="date-range-selector">
            <label htmlFor="date-range-select">Date Range: </label>
            <select
              id="date-range-select"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              <option value="last_7_days">Last 7 days</option>
              <option value="last_30_days">Last 30 days</option>
              <option value="past_month">Past month</option>
              <option value="this_month">This Month</option>
              <option value="last_3_months">Last 3 months</option>
              <option value="last_1_year">Last 1 year</option>
              <option value="ytd">YTD</option>
              <option value="all">All</option>
            </select>
          </div>

          <div className="group-by-selector">
            <label htmlFor="group-by-select">Group By: </label>
            <select
              id="group-by-select"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
            >
              <option value="none">None</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
              <option value="underlying_symbol">Underlying Symbol</option>
            </select>
          </div>
        </div>

        {data.grouped ? (
          <div className="covered-calls-table-container">
            <table className="covered-calls-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Group</th>
                  <th>Count</th>
                  <th>Total P&L</th>
                </tr>
              </thead>
              <tbody>
                {data.groups.map((group, index) => {
                  const totalPnl = parseFloat(group.total_pnl || 0);
                  const pnlClass = totalPnl >= 0 ? 'positive' : 'negative';
                  const isExpanded = expandedGroups.has(group.group_key);
                  return (
                    <React.Fragment key={group.group_key || index}>
                      <tr 
                        className="group-header-row"
                        onClick={() => toggleGroup(group.group_key)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="expand-cell">
                          <span className="expand-icon">{isExpanded ? '−' : '+'}</span>
                        </td>
                        <td className="group-key-cell">{group.group_key}</td>
                        <td className="count-cell">{group.count}</td>
                        <td className={`pnl-cell ${pnlClass}`}>
                          {formatCurrency(group.total_pnl)}
                        </td>
                      </tr>
                      {isExpanded && (() => {
                        // Group items by underlying_symbol
                        const symbolGroups = {};
                        group.items.forEach(item => {
                          const symbol = item.underlying_symbol || 'UNKNOWN';
                          if (!symbolGroups[symbol]) {
                            symbolGroups[symbol] = {
                              symbol: symbol,
                              items: [],
                              count: 0,
                              totalPnl: 0
                            };
                          }
                          symbolGroups[symbol].items.push(item);
                          symbolGroups[symbol].count += 1;
                          symbolGroups[symbol].totalPnl += parseFloat(item.pnl || 0);
                        });

                        // Sort symbols alphabetically
                        const sortedSymbols = Object.keys(symbolGroups).sort();

                        return sortedSymbols.map((symbol, symbolIndex) => {
                          const symbolGroup = symbolGroups[symbol];
                          const symbolPnl = symbolGroup.totalPnl;
                          const symbolPnlClass = symbolPnl >= 0 ? 'positive' : 'negative';
                          const symbolKey = `${group.group_key}-${symbol}`;
                          const isSymbolExpanded = expandedSymbols.has(symbolKey);

                          return (
                            <React.Fragment key={symbolKey || symbolIndex}>
                              <tr 
                                className="symbol-group-header-row"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSymbolGroup(group.group_key, symbol);
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                <td className="expand-cell">
                                  <span className="expand-icon">{isSymbolExpanded ? '−' : '+'}</span>
                                </td>
                                <td className="symbol-group-cell">
                                  <strong>{symbol}</strong>
                                </td>
                                <td className="count-cell">{symbolGroup.count}</td>
                                <td className={`pnl-cell ${symbolPnlClass}`}>
                                  {formatCurrency(symbolPnl.toFixed(2))}
                                </td>
                              </tr>
                              {isSymbolExpanded && symbolGroup.items.map((item, itemIndex) => {
                                const itemPnl = parseFloat(item.pnl || 0);
                                const itemPnlClass = itemPnl >= 0 ? 'positive' : 'negative';
                                return (
                                  <tr key={item.id || itemIndex} className="group-item-row">
                                    <td className="expand-cell"></td>
                                    <td className="item-cell">
                                      <div className="item-details">
                                        <div><strong>CONID:</strong> {item.conid || '-'}</div>
                                        <div><strong>Underlying:</strong> {item.underlying_symbol || '-'}</div>
                                        <div><strong>Account:</strong> {item.account_display_name || item.account_id}</div>
                                        <div><strong>Open:</strong> {formatDate(item.open_date)}</div>
                                        <div><strong>Close:</strong> {formatDate(item.close_date)}</div>
                                        <div><strong>Expiry:</strong> {formatDate(item.expiry_date)}</div>
                                        <div><strong>Days Open:</strong> {item.days_open !== null ? item.days_open : '-'}</div>
                                        <div><strong>% of Expiry:</strong> {formatPercentage(item.percentage_open)}</div>
                                      </div>
                                    </td>
                                    <td className="count-cell">1</td>
                                    <td className={`pnl-cell ${itemPnlClass}`}>
                                      {formatCurrency(item.pnl)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </React.Fragment>
                  );
                })}
                <tr className="totals-row">
                  <td className="totals-cell"></td>
                  <td className="totals-cell"><strong>Total</strong></td>
                  <td className="totals-cell"><strong>{data.total_count}</strong></td>
                  <td className={`totals-cell pnl-cell ${parseFloat(data.total_pnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                    <strong>{formatCurrency(data.total_pnl)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="covered-calls-table-container">
            <table className="covered-calls-table">
              <thead>
                <tr>
                  <th>CONID</th>
                  <th>Underlying</th>
                  <th>Account</th>
                  <th>Open Date</th>
                  <th>Close Date</th>
                  <th>Expiry</th>
                  <th>Days Open</th>
                  <th>% of Expiry</th>
                  <th>P&L</th>
                </tr>
              </thead>
              <tbody>
                {data.items && data.items.length > 0 ? (
                  data.items.map((item, index) => {
                    const pnl = parseFloat(item.pnl || 0);
                    const pnlClass = pnl >= 0 ? 'positive' : 'negative';
                    return (
                      <tr key={item.id || index}>
                        <td className="conid-cell">{item.conid || '-'}</td>
                        <td className="underlying-cell">{item.underlying_symbol || '-'}</td>
                        <td className="account-cell">{item.account_display_name || item.account_id}</td>
                        <td className="date-cell">{formatDate(item.open_date)}</td>
                        <td className="date-cell">{formatDate(item.close_date)}</td>
                        <td className="date-cell">{formatDate(item.expiry_date)}</td>
                        <td className="number-cell">{item.days_open !== null ? item.days_open : '-'}</td>
                        <td className="number-cell">{formatPercentage(item.percentage_open)}</td>
                        <td className={`pnl-cell ${pnlClass}`}>
                          {formatCurrency(item.pnl)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="empty-state">No covered calls found</td>
                  </tr>
                )}
                {data.items && data.items.length > 0 && (
                  <tr className="totals-row">
                    <td className="totals-cell" colSpan="8"><strong>Total</strong></td>
                    <td className={`totals-cell pnl-cell ${parseFloat(data.total_pnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                      <strong>{formatCurrency(data.total_pnl)}</strong>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CoveredCalls;

